/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
	Box,
	Group,
	Pagination as NativePagination,
	NumberInput,
	Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import classes from './Pagination.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type TPaginationProps = {
	total: number;
	totalItems?: number;
	boundaries?: number;
	activePage?: number;
	pageSize?: number;
	maxPageSize?: number;
	onChange?: (page: number) => void;
	onPageSizeChange: (size: number | string) => void;
};

/* -------------------------------------------------------------------------- */
/*                               Helper Functions                              */
/* -------------------------------------------------------------------------- */
export function formatPaginationInfo(
	currentPage = 1,
	pageSize = 10,
	totalItems = 0
): string {
	const startIndex = (currentPage - 1) * pageSize + 1;
	const endIndex = Math.min(
		startIndex + pageSize - 1,
		totalItems
	);

	return `${startIndex.toString().padStart(2, '0')} – ${endIndex
		.toString()
		.padStart(2, '0')} of ${totalItems}`;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Pagination = ({
	total,
	totalItems = 0,
	activePage = 1,
	pageSize = 100,
	maxPageSize = 100,
	onChange,
	onPageSizeChange,
}: TPaginationProps) => {
	const { t } = useTranslation();

	const paginationInfo = formatPaginationInfo(
		activePage,
		pageSize,
		totalItems
	);

	return (
		<Box className={classes.container}>
			<Group
				wrap="nowrap"
				gap="var(--mantine-spacing-xxs)"
			>
				<Text className={classes.selectPageNumber}>
					{t('global.linesPerPage')}
				</Text>

				<NumberInput
					classNames={classes}
					data-testid="pagination-number-input"
					min={0}
					max={maxPageSize}
					clampBehavior="strict"
					variant="unstyled"
					className={classes.selectPageNumberContainer}
					hideControls
					value={pageSize}
					onChange={onPageSizeChange}
				/>

				<Text className={classes.selectPageNumber}>
					{paginationInfo}
				</Text>
			</Group>

			<Group>
				<Text className={classes.selectPageNumber}>
					{t('global.selectPage')}
				</Text>
				<NativePagination
					radius={48}
					variant="outline"
					value={activePage}
					classNames={classes}
					onChange={onChange}
					total={total}
				/>
			</Group>
		</Box>
	);
};
